import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import AppLayout from './components/AppLayout';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MyDayPage from './pages/MyDayPage';
import CallsPage from './pages/CallsPage';
import CandidatesPage from './pages/CandidatesPage';
import AttendancePage from './pages/AttendancePage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import InterviewsPage from './pages/InterviewsPage';
import JobsPage from './pages/JobsPage';
import OffersPage from './pages/OffersPage';
import PlacementsPage from './pages/PlacementsPage';
import TrackerPage from './pages/TrackerPage';
import LeavePage from './pages/LeavePage';
import AttendanceRecordsPage from './pages/AttendanceRecordsPage';
import TeamPage from './pages/TeamPage';
import TimesheetsPage from './pages/TimesheetsPage';
import MyProfilePage from './pages/MyProfilePage';
import PayslipsPage from './pages/PayslipsPage';
import BillingPage from './pages/admin/BillingPage';
import CompaniesPage from './pages/admin/CompaniesPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import ExportsPage from './pages/admin/ExportsPage';
import CustomFieldsPage from './pages/admin/CustomFieldsPage';
import InvoicesPage from './pages/admin/InvoicesPage';
import OverviewPage from './pages/admin/OverviewPage';
import PaySetupPage from './pages/admin/PaySetupPage';
import PayrollPage from './pages/admin/PayrollPage';
import UsersPage from './pages/admin/UsersPage';
import ProfileReviewsPage from './pages/admin/ProfileReviewsPage';
import EmployeeDetailPage from './pages/admin/EmployeeDetailPage';
import WorkplacesPage from './pages/admin/WorkplacesPage';
import { isCompanyAdmin, isOwner, isStaff, isSupervisor, useAuth } from './store/auth';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

function RequireCompanyAdmin({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  if (!isCompanyAdmin(user)) {
    return (
      <div className="card text-center">
        <h2 className="text-base font-semibold text-gray-900">Not Available</h2>
        <p className="mt-1 text-sm text-gray-500">
          This page is limited to company administrators and the owner.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * Recruitment screens, closed to an employee.
 *
 * The server refuses these routes outright, so without this an employee who
 * typed the URL would get a page that renders and then fails every request on
 * it — a screen of error toasts rather than an answer. Saying so plainly is
 * the same courtesy the other two guards already extend.
 */
function RequireOwner({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  if (!isOwner(user)) {
    return (
      <div className="card text-center">
        <h2 className="text-base font-semibold text-gray-900">Not Available</h2>
        <p className="mt-1 text-sm text-gray-500">
          This page is limited to the owner.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

function RequireStaff({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  if (!isStaff(user)) {
    return (
      <div className="card text-center">
        <h2 className="text-base font-semibold text-gray-900">Not Available</h2>
        <p className="mt-1 text-sm text-gray-500">
          This page is part of recruitment. Your day, attendance, leave,
          timesheets and payslips are all on the bar above.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * Where the brand in the top-left lands, which is not one place.
 *
 * My Day is a personal screen: today's shift, this person's follow-ups, the
 * candidates they promised to ring. That is the right home for everybody who
 * has a day of their own to work.
 *
 * An owner does not. They have no shift, no worklist and no follow-ups, so
 * My Day renders as a series of empty panels — the least useful screen in the
 * product for the one account that reads across every company. The Daily Calls
 * Report is what they actually open it to see.
 *
 * A redirect rather than a different element, so the address bar says /calls.
 * Rendering the report at "/" would leave an owner unable to link anybody to
 * what they were looking at, and would give the same page two URLs.
 */
function HomeRoute() {
  const user = useAuth((s) => s.user);

  if (isOwner(user)) return <Navigate to="/calls" replace />;
  return <MyDayPage />;
}

function RequireSupervisor({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  if (!isSupervisor(user)) {
    return (
      <div className="card text-center">
        <h2 className="text-base font-semibold text-gray-900">Not Available</h2>
        <p className="mt-1 text-sm text-gray-500">
          This page is limited to managers and above.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

export default function App() {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  const refreshUser = useAuth((s) => s.refreshUser);

  // Re-read the user on load so a role change or deactivation applies
  // immediately rather than at the next login.
  useEffect(() => {
    if (isAuthenticated) void refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Public by necessity: whoever follows a reset link cannot sign in,
          which is the whole reason the link exists. */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<HomeRoute />} />
        <Route
          path="calls"
          element={
            <RequireStaff>
              <CallsPage />
            </RequireStaff>
          }
        />
        <Route
          path="candidates"
          element={
            <RequireStaff>
              <CandidatesPage />
            </RequireStaff>
          }
        />
        <Route path="attendance" element={<AttendancePage />} />
        {/* Anyone signed in can file their own leave and read the holiday
            list; approving and editing holidays gate themselves inside. */}
        <Route path="leave" element={<LeavePage />} />
        <Route
          path="clients"
          element={
            <RequireStaff>
              <ClientsPage />
            </RequireStaff>
          }
        />
        <Route
          path="clients/:clientId"
          element={
            <RequireStaff>
              <ClientDetailPage />
            </RequireStaff>
          }
        />
        {/* The recruiter's own pipeline, so no role guard beyond signing in —
            the server scopes it to whatever the viewer owns. */}
        <Route
          path="interviews"
          element={
            <RequireStaff>
              <InterviewsPage />
            </RequireStaff>
          }
        />
        {/* Postings are company-level; the API scopes what each role sees. */}
        <Route
          path="jobs"
          element={
            <RequireStaff>
              <JobsPage />
            </RequireStaff>
          }
        />
        <Route
          path="offers"
          element={
            <RequireStaff>
              <OffersPage />
            </RequireStaff>
          }
        />
        {/* Onboarding gates itself inside — the server requires an approver. */}
        <Route
          path="placements"
          element={
            <RequireStaff>
              <PlacementsPage />
            </RequireStaff>
          }
        />
        {/* A report over the same placements, in the tracker sheet's
            column order. No role guard: the server scopes the list, so a
            recruiter opening it sees their own rows and nobody else's. */}
        <Route
          path="tracker"
          element={
            <RequireStaff>
              <TrackerPage />
            </RequireStaff>
          }
        />
        {/* Anyone signed in files their own week; approving gates itself
            inside, and the server scopes the list to what the viewer owns. */}
        <Route path="timesheets" element={<TimesheetsPage />} />
        <Route path="payslips" element={<PayslipsPage />} />
        {/* No guard: everybody has a profile, and the route answers
            for itself when no employee record is linked. */}
        <Route path="profile" element={<MyProfilePage />} />
        {/* Owner only, and refused by the server too — the worklist behind
            it is SuperAdmin. /admin/billing is untouched and still serves
            managers and admins. */}
        <Route
          path="admin/invoices"
          element={
            <RequireOwner>
              <InvoicesPage />
            </RequireOwner>
          }
        />
        <Route
          path="admin/custom-fields"
          element={
            <RequireOwner>
              <CustomFieldsPage />
            </RequireOwner>
          }
        />
        <Route
          path="team"
          element={
            <RequireSupervisor>
              <TeamPage />
            </RequireSupervisor>
          }
        />
        {/* Same guard as Team, and the server applies it again: the roster it
            reads is company-scoped for a manager and picker-scoped for the
            owner. */}
        <Route
          path="attendance-records"
          element={
            <RequireSupervisor>
              <AttendanceRecordsPage />
            </RequireSupervisor>
          }
        />
        <Route
          path="admin/overview"
          element={
            <RequireCompanyAdmin>
              <OverviewPage />
            </RequireCompanyAdmin>
          }
        />
        <Route
          path="admin/payroll"
          element={
            <RequireCompanyAdmin>
              <PayrollPage />
            </RequireCompanyAdmin>
          }
        />
        <Route
          path="admin/pay-setup"
          element={
            <RequireCompanyAdmin>
              <PaySetupPage />
            </RequireCompanyAdmin>
          }
        />
        {/* Managers may read billing — chasing payment is their job — so this
            is supervisor-gated, and the write controls inside gate themselves
            on company_admin to match what the API allows. */}
        <Route
          path="admin/billing"
          element={
            <RequireSupervisor>
              <BillingPage />
            </RequireSupervisor>
          }
        />
        <Route
          path="admin/companies"
          element={
            <RequireSupervisor>
              <CompaniesPage />
            </RequireSupervisor>
          }
        />
        {/* Approver-only server-side: bulk extraction is a different act from
            reading the same rows on screen. The page re-checks inside too. */}
        {/* Company-admin, matching the API: this is role changes and
            password resets, not the approvals a manager already sees. */}
        <Route
          path="admin/audit-logs"
          element={
            <RequireCompanyAdmin>
              <AuditLogPage />
            </RequireCompanyAdmin>
          }
        />
        <Route
          path="admin/exports"
          element={
            <RequireSupervisor>
              <ExportsPage />
            </RequireSupervisor>
          }
        />
        <Route
          path="admin/users"
          element={
            <RequireSupervisor>
              <UsersPage />
            </RequireSupervisor>
          }
        />
        {/* The full employee record — bank, PAN, Aadhaar and every document.
            Company-admin and owner only, matching the audited API behind it. */}
        <Route
          path="admin/users/:userId"
          element={
            <RequireCompanyAdmin>
              <EmployeeDetailPage />
            </RequireCompanyAdmin>
          }
        />
        <Route
          path="admin/workplaces"
          element={
            <RequireSupervisor>
              <WorkplacesPage />
            </RequireSupervisor>
          }
        />
        {/* Company-admin and owner: this screen shows full bank, PAN and
            Aadhaar, and the API gates it the same way. */}
        <Route
          path="admin/profile-reviews"
          element={
            <RequireCompanyAdmin>
              <ProfileReviewsPage />
            </RequireCompanyAdmin>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
